import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	TextControl,
	ToggleControl,
	RangeControl,
	FormTokenField,
	ButtonGroup,
	Button,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { SEARCH_CRITERIA, WIDTH_OPTIONS } from '../constants';
import StyleControls from './style-controls';
import { resolvePresetValue } from '../../../utils/helpers';

export default function Edit( { attributes, setAttributes, clientId } ) {
	const blockProps = useBlockProps( {
		className: 'brandy-ajax-search-editor',
	} );

	const {
		searchCriteria,
		placeholder,
		showImages,
		showPrice,
		resultsLimit,
		suggestions,
		width,
	} = attributes;

	// Build CSS custom properties for editor preview.
	const styleVars = {
		'--brandy-ajax-search-input-color': resolvePresetValue( attributes.inputTextColor ) || undefined,
		'--brandy-ajax-search-input-font-size': attributes.inputFontSize || undefined,
		'--brandy-ajax-search-btn-bg': resolvePresetValue( attributes.buttonBgColor ) || undefined,
		'--brandy-ajax-search-btn-icon': resolvePresetValue( attributes.buttonIconColor ) || undefined,
		'--brandy-ajax-search-btn-size': `${ attributes.buttonSize }px`,
		'--brandy-ajax-search-btn-radius': `${ attributes.buttonBorderRadius }px`,
		'--brandy-ajax-search-btn-gap': `${ attributes.buttonGap }px`,
		'--brandy-ajax-search-chip-bg': resolvePresetValue( attributes.chipBgColor ) || undefined,
		'--brandy-ajax-search-chip-color': resolvePresetValue( attributes.chipTextColor ) || undefined,
		'--brandy-ajax-search-chip-font-size': attributes.chipFontSize || undefined,
		'--brandy-ajax-search-chip-radius': `${ attributes.chipBorderRadius }px`,
		'--brandy-ajax-search-chip-gap': `${ attributes.chipGap }px`,
		'--brandy-ajax-search-suggestions-mt': `${ attributes.suggestionsMarginTop }px`,
	};
	const inputWrapperStyleVars = {
		'--brandy-ajax-search-wrapper-bg': resolvePresetValue( attributes.wrapperBgColor ) || undefined,
		'--brandy-ajax-search-wrapper-radius': `${ attributes.wrapperBorderRadius }px`,
		'--brandy-ajax-search-wrapper-height': `${ attributes.wrapperHeight }px`,
	}

	// Parse border object from BorderBoxControl.
	const border = attributes.wrapperBorder || {};
	const bColor = border.color ?? border.top?.color;
	const bWidth = border.width ?? border.top?.width;
	const bStyle = border.style ?? border.top?.style;
	if ( bColor ) {
		inputWrapperStyleVars[ '--brandy-ajax-search-wrapper-border-color' ] = bColor;
	}
	if ( bWidth ) {
		inputWrapperStyleVars[ '--brandy-ajax-search-wrapper-border-width' ] = bWidth;
	}
	if ( bStyle ) {
		inputWrapperStyleVars[ '--brandy-ajax-search-wrapper-border-style' ] = bStyle;
	}

	if ( attributes.inputPlaceholderColor ) {
		styleVars[ '--brandy-ajax-search-input-placeholder' ] =
			attributes.inputPlaceholderColor;
		styleVars[ '--brandy-ajax-search-placeholder-opacity' ] = '1';
	}

	const showProductOptions =
		searchCriteria === SEARCH_CRITERIA.ALL ||
		searchCriteria === SEARCH_CRITERIA.PRODUCTS;

	const criteriaOptions = [
		{
			label: __( 'All (Posts & Products)', 'brandy-blocks' ),
			value: SEARCH_CRITERIA.ALL,
		},
		{
			label: __( 'Posts Only', 'brandy-blocks' ),
			value: SEARCH_CRITERIA.POSTS,
		},
		{
			label: __( 'Products Only', 'brandy-blocks' ),
			value: SEARCH_CRITERIA.PRODUCTS,
		},
	];

	return (
		<div { ...blockProps }>
			<InspectorControls>
				<PanelBody title={ __( 'Search Settings', 'brandy-blocks' ) }>
					<SelectControl
						label={ __( 'Search Criteria', 'brandy-blocks' ) }
						value={ searchCriteria }
						options={ criteriaOptions }
						onChange={ ( value ) =>
							setAttributes( { searchCriteria: value } )
						}
					/>
					<TextControl
						label={ __( 'Placeholder Text', 'brandy-blocks' ) }
						value={ placeholder }
						onChange={ ( value ) =>
							setAttributes( { placeholder: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Images', 'brandy-blocks' ) }
						checked={ showImages }
						onChange={ ( value ) =>
							setAttributes( { showImages: value } )
						}
					/>
					{ showProductOptions && (
						<ToggleControl
							label={ __( 'Show Price', 'brandy-blocks' ) }
							checked={ showPrice }
							onChange={ ( value ) =>
								setAttributes( { showPrice: value } )
							}
						/>
					) }
					<RangeControl
						label={ __( 'Results Limit', 'brandy-blocks' ) }
						value={ resultsLimit }
						onChange={ ( value ) =>
							setAttributes( { resultsLimit: value } )
						}
						min={ 1 }
						max={ 20 }
					/>
					<div className="brandy-ajax-search-width-control">
						<p className="components-base-control__label">
							{ __( 'Width', 'brandy-blocks' ) }
						</p>
						<ButtonGroup>
							{ WIDTH_OPTIONS.map( ( option ) => (
								<Button
									key={ option }
									variant={
										width === option
											? 'primary'
											: 'secondary'
									}
									onClick={ () =>
										setAttributes( { width: option } )
									}
								>
									{ option }%
								</Button>
							) ) }
						</ButtonGroup>
					</div>
					<FormTokenField
						label={ __(
							'Suggestion Keywords',
							'brandy-blocks'
						) }
						value={ suggestions }
						onChange={ ( tokens ) =>
							setAttributes( { suggestions: tokens } )
						}
						__experimentalShowHowTo={ false }
					/>
				</PanelBody>
			</InspectorControls>

			<StyleControls
				attributes={ attributes }
				setAttributes={ setAttributes }
				clientId={ clientId }
			/>

			{ /* Static preview in editor */ }
			<div
				className="brandy-ajax-search"
				style={ { maxWidth: `${ width }%`, ...styleVars } }
			>
				<div className="brandy-ajax-search__input-wrapper" style={ {...inputWrapperStyleVars} }>
					<input
						className="brandy-ajax-search__input"
						type="text"
						placeholder={ placeholder }
						disabled
					/>
					<button className="brandy-ajax-search__button" disabled>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							width="20"
							height="20"
						>
							<path d="M13.5 6C10.5 6 8 8.5 8 11.5c0 1.1.3 2.1.9 3l-3.4 3.4c-.4.4-.4 1 0 1.4.2.2.5.3.7.3s.5-.1.7-.3l3.4-3.4c.9.6 1.9.9 3 .9 3 0 5.5-2.5 5.5-5.5S16.5 6 13.5 6zm0 9c-1.9 0-3.5-1.6-3.5-3.5S11.6 8 13.5 8 17 9.6 17 11.5 15.4 15 13.5 15z" />
						</svg>
					</button>
				</div>
				{ suggestions.length > 0 && (
					<div className="brandy-ajax-search__suggestions">
						{ suggestions.map( ( keyword ) => (
							<span
								key={ keyword }
								className="brandy-ajax-search__suggestion-chip"
							>
								{ keyword }
							</span>
						) ) }
					</div>
				) }
			</div>
		</div>
	);
}
